
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

import Login from './pages/Login/Login';
import Register from './pages/Login/Register';
import Dashboard from './pages/Student/Dashboard';
import QuizResults from './pages/Student/QuizResults';
import QuizSubmission from './pages/Student/QuizSubmission';
import StudentProfile from './pages/Student/StudentProfile';
// import Course from './pages/Student/Course';
import MyCourses from './pages/Student/MyCourses';
import AddCollege from './pages/Admin/AddCollege';
import AddCourse from './pages/Admin/AddCourse';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AssignCourseInstitute from './pages/Admin/AssignCourseInstitute';
import AssignCourseStudent from './pages/Admin/AssignCourseStudent';
import AddSection from './pages/Admin/AddSection';
import AddRole from './pages/Admin/AddRole';
import AddLesson from './pages/Admin/AddLesson';
import AuditLogDetails from './pages/Admin/AuditLogDetails';
import NotificationCenter from './pages/Admin/NotificationCenter';
import AddQuiz from './pages/Admin/AddQuiz';
import Voucher from './pages/Admin/Voucher';
import AdminSupportTicket from './pages/Admin/AdminSupportTicket';
import AddQuizQuestion from './pages/Admin/AddQuizQuestion';
import TicketDetails from './pages/Admin/TicketDetails';
import ChangePassword from './pages/Admin/ChangePassword';
import Users from './pages/Admin/Users';
import ForgotPassword from './pages/Password/ForgotPassword';
import ResetPassword from './pages/Password/ResetPassword';
import AddStudent from './pages/Institute/AddStudent';
import AssignCourse from './pages/Institute/AssignCourse';
import Payment from './pages/Institute/Payment';
import InstituteProfile from './pages/Institute/InstituteProfile';
import PaymentSuccess from './pages/Institute/PaymentSuccess';
import InstructorProfile from './pages/Institute/InstructorProfile';
import InstituteDashboard from './pages/Institute/InstituteDashboard';
import SupportTicket from './pages/Institute/SupportTicket';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import NotFound from './components/NotFound';
import Maintenance from "./pages/Maintenance";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";
const MAINTENANCE_PROBE_URL = API_BASE_URL.replace(/\/$/, "") + "/maintenance-status";

const App = () => {
    // ✅ Reactive state — not a static constant — so it responds when the
    //    axios interceptor sets "maintenance" in localStorage mid-session.
    const [isMaintenance, setIsMaintenance] = useState(
        localStorage.getItem("maintenance") === "true"
    );
    const [isBootstrapping, setIsBootstrapping] = useState(true);

    useEffect(() => {
        // Listen for localStorage changes triggered by the axios interceptor
        // (works across tabs and also within the same tab via our custom event)
        const handleStorage = () => {
            setIsMaintenance(localStorage.getItem("maintenance") === "true");
        };

        window.addEventListener("storage", handleStorage);
        window.addEventListener("maintenanceChange", handleStorage);

        return () => {
            window.removeEventListener("storage", handleStorage);
            window.removeEventListener("maintenanceChange", handleStorage);
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const checkMaintenanceOnLoad = async () => {
            try {
                const res = await fetch(MAINTENANCE_PROBE_URL, { method: "GET" });
                if (res.status === 503) {
                    localStorage.setItem("maintenance", "true");
                    if (isMounted) {
                        setIsMaintenance(true);
                    }
                } else if (res.ok) {
                    localStorage.removeItem("maintenance");
                    if (isMounted) {
                        setIsMaintenance(false);
                    }
                }
            } catch {
                // Ignore load-time probe errors; runtime interceptor still handles 503.
            } finally {
                if (isMounted) {
                    setIsBootstrapping(false);
                }
            }
        };

        checkMaintenanceOnLoad();

        return () => {
            isMounted = false;
        };
    }, []);

    if (isBootstrapping) {
        // return <div>Loading...</div>;
    }

    return (
        <Router>
            <Routes>

                {isMaintenance ? (
                    // ✅ ONLY maintenance page — no other routes accessible
                    <>
                        <Route path="/maintenance" element={<Maintenance />} />
                        <Route path="*" element={<Maintenance />} />
                    </>
                ) : (
                    <>
                        {/* Public Route */}
                        <Route path="/maintenance" element={<Maintenance />} />
                        <Route path="/" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password/:userId/:token" element={<ResetPassword />} />

                        {/* Admin-only routes */}
                        <Route element={<ProtectedRoute allowedRoles={['Admin']} />}>
                            <Route path="/add-college" element={<AddCollege />} />
                            <Route path="/add-role" element={<AddRole />} />
                            <Route path="/users" element={<Users />} />
                            {/* <Route path="/add-department" element={<AddDepartment />} /> */}
                            <Route path="/add-course" element={<AddCourse />} />
                            <Route path="/admin-dashboard" element={<AdminDashboard />} />
                            <Route path="/add-section" element={<AddSection />} />
                            <Route path="/add-lesson" element={<AddLesson />} />
                            <Route path="/admin/assign-course" element={<AssignCourseInstitute />} />
                            <Route path="/admin/course-assignment" element={<AssignCourseStudent />} />
                            <Route path="/add-quiz" element={<AddQuiz />} />
                            <Route path="/add-quiz-question" element={<AddQuizQuestion />} />
                            <Route path="/voucher" element={<Voucher />} />
                            <Route path="/admin/support-ticket" element={<AdminSupportTicket />} />
                            <Route path="/admin/audit-log-details" element={<AuditLogDetails />} />
                            <Route path="/admin/notification-center" element={<NotificationCenter />} />
                        </Route>

                        {/* Student-only routes */}
                        <Route element={<ProtectedRoute allowedRoles={['Students']} />}>
                            {/* <Route path="/dashboard" element={<Dashboard />} /> */}
                            <Route path="/quiz-submission" element={<QuizSubmission />} />
                            <Route path="/quiz-results" element={<QuizResults />} />
                            <Route path="/student-profile" element={<StudentProfile />} />
                            {/* <Route path="/course" element={<Course />} /> */}
                            {/* <Route path="/my-courses" element={<MyCourses />} /> */}
                        </Route>

                        {/* Institution-only routes */}
                        <Route element={<ProtectedRoute allowedRoles={['Institution']} />}>
                            <Route path="/add-student" element={<AddStudent />} />
                            {/* <Route path="/add-instructors" element={<AddInstructors />} /> */}
                            {/* <Route path="/institution/add-department" element={<InstituteAddDepartment />} /> */}
                            {/* <Route path="/institution/assign-course" element={<AssignCourse />} /> */}
                            <Route path="/payment" element={<Payment />} />
                            {/* <Route path="/course" element={<Course />} /> */}
                            <Route path="/institute-profile" element={<InstituteProfile />} />
                            <Route path="/institute-dashboard" element={<InstituteDashboard />} />
                            <Route path="/support-ticket" element={<SupportTicket />} />
                        </Route>

                        {/* Instructors-only routes */}
                        <Route element={<ProtectedRoute allowedRoles={['Instructors']} />}>
                            <Route path="/instructor-profile" element={<InstructorProfile />} />
                        </Route>

                        {/* Institution and student routes */}
                        <Route element={<ProtectedRoute allowedRoles={['Institution', 'Students']} />}>
                            {/* <Route path="/dashboard" element={<Dashboard />} /> */}

                            <Route path="/my-courses" element={<MyCourses />} />
                            <Route path="/payment-success" element={<PaymentSuccess />} />
                        </Route>

                        {/* Institution and student routes */}
                        <Route element={<ProtectedRoute allowedRoles={['Students', 'Instructors']} />}>
                            <Route path="/dashboard" element={<Dashboard />} />
                        </Route>

                        {/* Institution and Admin routes */}
                        <Route element={<ProtectedRoute allowedRoles={['Admin', 'Institution']} />}>
                            <Route path="/ticket-details" element={<TicketDetails />} />
                        </Route>

                        {/* Institution and Admin and student routes */}
                        <Route element={<ProtectedRoute allowedRoles={['Admin', 'Institution', 'Students']} />}>
                            <Route path="/change-password" element={<ChangePassword />} />
                        </Route>

                        {/* Catch-all route (Not Found) */}
                        <Route path="*" element={<NotFound />} />
                    </>
                )}
            </Routes>
        </Router>
    );
};

export default App;
